
import React, { useState, useEffect } from 'react';
import MonthPicker from './MonthPicker';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchContacts,
  selectAllContacts,
  selectContactsLoading,
  selectContactsStats,
} from '../../Redux/contactsSlice';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Stack,
  Button,
  Chip,
  IconButton,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
  CircularProgress,
} from '@mui/material';
import {
  Person,
  Business,
  TrendingUp,
  Assessment,
  LocationOn,
  PersonAdd,
  ArrowForward,
} from '@mui/icons-material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

export const Dashboard = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Redux state
  const contacts = useSelector(selectAllContacts);
  const loading = useSelector(selectContactsLoading);
  const stats = useSelector(selectContactsStats);

  const [selectedMonth, setSelectedMonth] = useState(new Date());

  // Format date for API
  const formatMonthYear = (date) => {
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }).replace(' ', '-');
  };

  // Load contacts on mount
  useEffect(() => {
    const monthYear = formatMonthYear(selectedMonth);
    dispatch(fetchContacts({ country: 'India', monthYear }));
  }, [dispatch, selectedMonth]);

  // Calculate additional analytics
  const getRecentContacts = () => {
    return [...contacts]
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      .slice(0, 5);
  };

  const getTopCities = () => {
    const cityCounts = contacts.reduce((acc, contact) => {
      const city = contact.city || 'Unknown';
      acc[city] = (acc[city] || 0) + 1;
      return acc;
    }, {});

    return Object.entries(cityCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);
  };

  const getContactGrowth = () => {
    // Group contacts by day (last 7 days)
    const today = new Date();
    const last7Days = [];
    
    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      last7Days.push({
        date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        count: 0,
      });
    }

    contacts.forEach(contact => {
      const contactDate = new Date(contact.createdAt);
      const dayIndex = last7Days.findIndex(day => {
        const dayDate = new Date(day.date + ', ' + today.getFullYear());
        return contactDate.toDateString() === dayDate.toDateString();
      });
      
      if (dayIndex !== -1) {
        last7Days[dayIndex].count++;
      }
    });

    return last7Days;
  };

  const recentContacts = getRecentContacts();
  const topCities = getTopCities();
  const growthData = getContactGrowth();

  // Chart configurations
  const lineChartData = {
    labels: growthData.map(d => d.date),
    datasets: [
      {
        label: 'Contacts Added',
        data: growthData.map(d => d.count),
        borderColor: '#3b82f6',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        fill: true,
        tension: 0.4,
        pointBackgroundColor: '#3b82f6',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
      },
    ],
  };

  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: '#1e293b',
        padding: 12,
        titleFont: { size: 13, weight: '600' },
        bodyFont: { size: 12 },
        cornerRadius: 8,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          precision: 0,
          color: '#64748b',
          font: { size: 11 },
        },
        grid: {
          color: '#f1f5f9',
        },
      },
      x: {
        ticks: {
          color: '#64748b',
          font: { size: 11 },
        },
        grid: {
          display: false,
        },
      },
    },
  };

  const cityChartData = {
    labels: topCities.map(([city]) => city),
    datasets: [
      {
        label: 'Contacts',
        data: topCities.map(([, count]) => count),
        backgroundColor: [
          '#3b82f6',
          '#8b5cf6',
          '#10b981',
          '#f59e0b',
          '#ef4444',
        ],
        borderWidth: 0,
      },
    ],
  };

  const cityChartOptions = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: '#1e293b',
        padding: 12,
        cornerRadius: 8,
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        ticks: {
          precision: 0,
          color: '#64748b',
          font: { size: 11 },
        },
        grid: {
          color: '#f1f5f9',
        },
      },
      y: {
        ticks: {
          color: '#64748b',
          font: { size: 11 },
        },
        grid: {
          display: false,
        },
      },
    },
  };

  const typeDistributionData = {
    labels: ['Individuals', 'Companies'],
    datasets: [
      {
        data: [stats.personContacts, stats.companyContacts],
        backgroundColor: ['#3b82f6', '#8b5cf6'],
        borderWidth: 0,
      },
    ],
  };

  const typeDistributionOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          padding: 15,
          font: { size: 12, weight: '600' },
          color: '#1e293b',
        },
      },
      tooltip: {
        backgroundColor: '#1e293b',
        padding: 12,
        cornerRadius: 8,
      },
    },
  };

  const getContactName = (contact) => {
    return contact.contactType === 'company' ? contact.companyName : contact.fullName;
  };

  const getContactEmail = (contact) => {
    return contact.contactType === 'company' ? contact.companyEmail : contact.email;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: '#f8fafc' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', py: 2 }}>
      <Container maxWidth="xl">
        {/* Header */}
        <Box sx={{ mb: 2 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b' }}>
                Dashboard Overview
              </Typography>
              <MonthPicker value={selectedMonth} onChange={setSelectedMonth} />
              <Typography variant="body2" sx={{ color: '#64748b', ml: 1 }}>
                {selectedMonth.toLocaleString('default', { month: 'long', year: 'numeric' })}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<Assessment />}
                onClick={() => navigate('/contact-management')}
                sx={{
                  borderColor: '#e2e8f0',
                  color: '#64748b',
                  fontWeight: 600,
                  px: 2,
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  '&:hover': {
                    borderColor: '#cbd5e1',
                    bgcolor: '#f8fafc',
                  },
                }}
              >
                All Contacts
              </Button>
              <Button
                size="small"
                variant="contained"
                startIcon={<PersonAdd />}
                onClick={() => navigate('/create-contact')}
                sx={{
                  bgcolor: '#3b82f6',
                  fontWeight: 600,
                  px: 2,
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  boxShadow: '0 2px 8px rgba(59, 130, 246, 0.25)',
                  '&:hover': {
                    bgcolor: '#2563eb',
                  },
                }}
              >
                Add Contact
              </Button>
            </Stack>
          </Stack>
        </Box>

        {/* Key Metrics */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6} sm={3}>
            <Card sx={{
              borderRadius: 2,
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
              border: '1px solid #f1f5f9',
            }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 1.5,
                      bgcolor: '#3b82f615',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#3b82f6',
                    }}
                  >
                    <Assessment sx={{ fontSize: 20 }} />
                  </Box>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b', lineHeight: 1 }}>
                      {stats.totalContacts}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#64748b' }}>
                      Total Contacts
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={3}>
            <Card sx={{
              borderRadius: 2,
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
              border: '1px solid #f1f5f9',
            }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 1.5,
                      bgcolor: '#3b82f615',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#3b82f6',
                    }}
                  >
                    <Person sx={{ fontSize: 20 }} />
                  </Box>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b', lineHeight: 1 }}>
                      {stats.personContacts}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#64748b' }}>
                      Individuals
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={3}>
            <Card sx={{
              borderRadius: 2,
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
              border: '1px solid #f1f5f9',
            }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 1.5,
                      bgcolor: '#8b5cf615',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#8b5cf6',
                    }}
                  >
                    <Business sx={{ fontSize: 20 }} />
                  </Box>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b', lineHeight: 1 }}>
                      {stats.companyContacts}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#64748b' }}>
                      Companies
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={3}>
            <Card sx={{
              borderRadius: 2,
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
              border: '1px solid #f1f5f9',
            }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 1.5,
                      bgcolor: '#10b98115',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#10b981',
                    }}
                  >
                    <TrendingUp sx={{ fontSize: 20 }} />
                  </Box>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b', lineHeight: 1 }}>
                      {stats.thisMonth || 0}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#64748b' }}>
                      This Month
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Charts Row */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {/* Contact Growth Trend */}
          <Grid item xs={12} lg={8}>
            <Paper sx={{ p: 2, borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #f1f5f9' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1e293b' }}>
                  Contact Growth (Last 7 Days)
                </Typography>
                <Chip
                  icon={<TrendingUp sx={{ fontSize: 16 }} />}
                  label={`${growthData.reduce((sum, d) => sum + d.count, 0)} total`}
                  size="small"
                  sx={{
                    bgcolor: '#3b82f615',
                    color: '#3b82f6',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                  }}
                />
              </Stack>
              <Box sx={{ height: 180 }}>
                <Line data={lineChartData} options={lineChartOptions} />
              </Box>
            </Paper>
          </Grid>

          {/* Contact Type Distribution */}
          <Grid item xs={12} lg={4}>
            <Paper sx={{ p: 2, borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #f1f5f9', height: '100%' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1e293b', mb: 1.5 }}>
                Type Distribution
              </Typography>
              <Box sx={{ height: 180, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Doughnut data={typeDistributionData} options={typeDistributionOptions} />
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Analytics Row */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {/* Top Cities */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #f1f5f9' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1e293b' }}>
                  Top Cities
                </Typography>
                <LocationOn sx={{ color: '#3b82f6', fontSize: 20 }} />
              </Stack>
              <Box sx={{ height: 180 }}>
                <Bar data={cityChartData} options={cityChartOptions} />
              </Box>
            </Paper>
          </Grid>

          {/* Recent Activity */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #f1f5f9', height: '100%' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1e293b' }}>
                  Recent Activity
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => navigate('/contact-management')}
                  sx={{
                    bgcolor: '#f8fafc',
                    '&:hover': { bgcolor: '#f1f5f9' },
                  }}
                >
                  <ArrowForward fontSize="small" />
                </IconButton>
              </Stack>
              <List sx={{ maxHeight: 200, overflow: 'auto' }}>
                {recentContacts.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 3 }}>
                    <Typography variant="body2" sx={{ color: '#64748b' }}>
                      No recent activity
                    </Typography>
                  </Box>
                ) : (
                  recentContacts.map((contact, index) => (
                    <React.Fragment key={contact.dateTimeStamp}>
                      <ListItem
                        sx={{
                          px: 0,
                          py: 1,
                          cursor: 'pointer',
                          borderRadius: 1.5,
                          transition: 'all 0.2s',
                          '&:hover': {
                            bgcolor: '#f8fafc',
                          },
                        }}
                        onClick={() => navigate(`/contact/${encodeURIComponent(contact.dateTimeStamp)}`)}
                      >
                        <ListItemAvatar>
                          <Avatar
                            sx={{
                              width: 32,
                              height: 32,
                              bgcolor: contact.contactType === 'person' ? '#3b82f615' : '#8b5cf615',
                              color: contact.contactType === 'person' ? '#3b82f6' : '#8b5cf6',
                            }}
                          >
                            {contact.contactType === 'person' ? <Person sx={{ fontSize: 18 }} /> : <Business sx={{ fontSize: 18 }} />}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b', fontSize: '0.875rem' }}>
                              {getContactName(contact)}
                            </Typography>
                          }
                          secondary={
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.25 }}>
                              <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.7rem' }}>
                                {getContactEmail(contact) || 'No email'}
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#cbd5e1', fontSize: '0.7rem' }}>•</Typography>
                              <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.7rem' }}>
                                {new Date(contact.createdAt).toLocaleDateString()}
                              </Typography>
                            </Stack>
                          }
                        />
                      </ListItem>
                      {index < recentContacts.length - 1 && <Divider component="li" />}
                    </React.Fragment>
                  ))
                )}
              </List>
              {recentContacts.length > 0 && (
                <Button
                  fullWidth
                  size="small"
                  variant="text"
                  endIcon={<ArrowForward />}
                  onClick={() => navigate('/contact-management')}
                  sx={{
                    mt: 1,
                    textTransform: 'none',
                    color: '#3b82f6',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    '&:hover': {
                      bgcolor: '#f8fafc',
                    },
                  }}
                >
                  View All
                </Button>
              )}
            </Paper>
          </Grid>
        </Grid>

        {/* Quick Actions */}
        <Paper sx={{ p: 2, borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #f1f5f9' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1e293b', mb: 1.5 }}>
            Quick Actions
          </Typography>
          <Grid container spacing={1.5}>
            <Grid item xs={6} sm={3}>
              <Button
                fullWidth
                size="small"
                variant="outlined"
                startIcon={<PersonAdd sx={{ fontSize: 18 }} />}
                onClick={() => navigate('/create-contact')}
                sx={{
                  py: 1,
                  borderColor: '#e2e8f0',
                  color: '#1e293b',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  '&:hover': {
                    borderColor: '#3b82f6',
                    bgcolor: '#3b82f615',
                  },
                }}
              >
                Add Contact
              </Button>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Button
                fullWidth
                size="small"
                variant="outlined"
                startIcon={<Assessment sx={{ fontSize: 18 }} />}
                onClick={() => navigate('/contact-management')}
                sx={{
                  py: 1,
                  borderColor: '#e2e8f0',
                  color: '#1e293b',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  '&:hover': {
                    borderColor: '#3b82f6',
                    bgcolor: '#3b82f615',
                  },
                }}
              >
                View All
              </Button>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Button
                fullWidth
                size="small"
                variant="outlined"
                startIcon={<Person sx={{ fontSize: 18 }} />}
                onClick={() => {
                  dispatch(fetchContacts({ country: 'India', monthYear: formatMonthYear(selectedMonth) }));
                  navigate('/contact-management');
                }}
                sx={{
                  py: 1,
                  borderColor: '#e2e8f0',
                  color: '#1e293b',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  '&:hover': {
                    borderColor: '#3b82f6',
                    bgcolor: '#3b82f615',
                  },
                }}
              >
                Individuals
              </Button>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Button
                fullWidth
                size="small"
                variant="outlined"
                startIcon={<Business sx={{ fontSize: 18 }} />}
                onClick={() => {
                  dispatch(fetchContacts({ country: 'India', monthYear: formatMonthYear(selectedMonth) }));
                  navigate('/contact-management');
                }}
                sx={{
                  py: 1,
                  borderColor: '#e2e8f0',
                  color: '#1e293b',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  '&:hover': {
                    borderColor: '#3b82f6',
                    bgcolor: '#3b82f615',
                  },
                }}
              >
                Companies
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Container>
    </Box>
  );
};

export default Dashboard;
